import React from "react";
import { cn } from "../../../lib/utils";
import { FOCUS_RING } from "./classes";

/**
 * The admin console's buttons, by variant (design §D, revised: the same as every admin
 * page). `md` / `full` use the product's `.btn-primary` / `.btn-ghost` (index.css), as
 * ProductsAdmin and SettingsAdmin do; `sm` is the admin's compact row-action pill
 * (BookingsAdmin's `text-xs px-3 py-1.5 rounded-full`). `link` is the admin text link;
 * `danger` the rose confirmation button of admin delete dialogs.
 *
 * `as` renders another element (a router `Link`) with the same look. A disabled button
 * keeps `aria-disabled` so assistive tech reads the state; its reason goes in a
 * `CrmTooltip` around it.
 */
const CLASSES = {
  primary: { md: "btn-primary text-sm", full: "btn-primary text-sm w-full justify-center", sm: "text-xs px-3 py-1.5 rounded-full bg-eh-ink text-eh-bg hover:bg-eh-ink/90" },
  outline: { md: "btn-ghost text-sm", full: "btn-ghost text-sm w-full justify-center", sm: "text-xs px-3 py-1.5 rounded-full border border-eh-border-soft text-eh-ink hover:bg-eh-bg" },
  dark: { md: "btn-primary text-sm", full: "btn-primary text-sm w-full justify-center", sm: "text-xs px-3 py-1.5 rounded-full bg-eh-ink text-eh-bg hover:bg-eh-ink/90" },
  ghost: { md: "text-sm px-3 py-2 rounded-full text-eh-ink hover:bg-eh-bg", full: "text-sm px-3 py-2 rounded-full text-eh-ink hover:bg-eh-bg w-full", sm: "text-xs px-2 py-1 rounded-full text-eh-ink hover:bg-eh-bg" },
  link: { md: "text-sm font-medium text-eh-ink underline-offset-4 hover:underline", full: "text-sm font-medium text-eh-ink underline-offset-4 hover:underline w-full", sm: "text-xs font-medium text-eh-ink underline-offset-4 hover:underline" },
  danger: { md: "text-sm px-5 py-2.5 rounded-full bg-rose-700 hover:bg-rose-800 text-white", full: "text-sm px-5 py-2.5 rounded-full bg-rose-700 hover:bg-rose-800 text-white w-full", sm: "text-xs px-3 py-1.5 rounded-full bg-rose-700 hover:bg-rose-800 text-white" },
};

const CrmButton = React.forwardRef(function CrmButton(
  { as: Tag = "button", variant = "primary", size = "md", className, disabled, type, children, ...rest },
  ref,
) {
  const isButton = Tag === "button";
  return (
    <Tag
      ref={ref}
      type={isButton ? type || "button" : undefined}
      disabled={isButton ? disabled : undefined}
      aria-disabled={disabled || undefined}
      className={cn(
        "inline-flex items-center justify-center gap-1.5 transition",
        "disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none aria-disabled:opacity-50",
        FOCUS_RING,
        (CLASSES[variant] || CLASSES.primary)[size] || CLASSES.primary.md,
        className,
      )}
      {...rest}
    >
      {children}
    </Tag>
  );
});

export default CrmButton;
